// src/components/WalletConnectorHelpers.jsx

import React from 'react';
import { getWalletSupportInfo } from '../utils/walletUtils';

/**
 * Get wallet networks based on wallet type
 * @param {string} type - The wallet type
 * @returns {string[]} List of supported networks
 */
export const getWalletNetworks = (type) => {
  const supportInfo = getWalletSupportInfo(type);
  
  if (supportInfo.supportsEVM && supportInfo.supportsSolana) {
    return ['ethereum', 'bsc', 'solana'];
  } else if (supportInfo.supportsSolana) {
    return ['solana'];
  } else {
    return ['ethereum', 'bsc'];
  }
};

/**
 * Get status badge for network support
 * @param {string} type - The wallet type
 * @returns {JSX.Element} Badge component
 */
export const getNetworkSupportBadge = (type) => {
  const supportInfo = getWalletSupportInfo(type);
  
  if (supportInfo.supportsEVM && supportInfo.supportsSolana) {
    return <span className="support-badge dual">EVM & Solana</span>;
  } else if (supportInfo.supportsSolana) {
    return <span className="support-badge solana">Solana Only</span>;
  } else {
    return <span className="support-badge evm">EVM Only</span>;
  }
};

/**
 * Renders an improved OKX installation guide component with more detailed steps
 * @param {Object} props - Component props
 * @param {Function} props.onTryAgain - Callback when "Try Again" is clicked
 * @param {Function} props.onCancel - Callback when "Cancel" is clicked
 * @returns {JSX.Element} OKX installation guide component
 */
export const OkxInstallationGuide = ({ onTryAgain, onCancel }) => (
  <div className="okx-guide-content">
    <h2>Enable Solana in OKX Wallet</h2>
    <div className="okx-guide-steps">
      <p>We couldn't connect to a Solana account in your OKX wallet. Please follow these steps:</p>
      
      <div className="okx-guide-section">
        <h3>1. Make sure OKX Wallet is properly set up</h3>
        <ul>
          <li>Ensure the OKX Wallet extension is installed and up to date</li>
          <li>Make sure you're logged in to your OKX Wallet</li>
          <li>Check that your wallet is unlocked (not password-protected)</li>
        </ul>
      </div>
      
      <div className="okx-guide-section">
        <h3>2. Enable Solana network</h3>
        <ol>
          <li>Open OKX Wallet extension</li>
          <li>Click the settings icon (gear) in the bottom-right corner</li>
          <li>Select "Networks" from the menu</li>
          <li>Find "Solana" in the list and make sure it's toggled ON</li>
          <li>Return to the main wallet screen</li>
        </ol>
      </div>
      
      <div className="okx-guide-section">
        <h3>3. Create or select a Solana account</h3>
        <ol>
          <li>In the OKX Wallet main screen, click on the account dropdown at the top</li>
          <li>Click the "+" icon or "Add account" button</li>
          <li>Select "Solana" as the network</li>
          <li>Choose "Create" for a new account or "Import" if you have an existing account</li>
          <li>Follow the instructions to create/import your Solana account</li>
          <li><strong>Important:</strong> Make sure the Solana account is selected as the active account</li>
        </ol>
      </div>
      
      <div className="okx-guide-note">
        <p><strong>Note:</strong> OKX Wallet manages Ethereum and Solana accounts separately. You must have a Solana account created and selected before connecting. The Solana address format is different from Ethereum addresses.</p>
      </div>
      
      <div className="okx-guide-section">
        <h3>4. Browser Permissions</h3>
        <ol>
          <li>Ensure that you've allowed the extension to access this website</li>
          <li>Sometimes refreshing the page and reopening the extension can help</li>
          <li>If using OKX mobile app, make sure you're using the in-app browser</li>
        </ol>
      </div>
      
      <div className="okx-guide-section">
        <h3>5. Verify connectivity</h3>
        <p>After completing the steps above:</p>
        <ol>
          <li>Make sure the Solana account is visible and selected in OKX Wallet</li>
          <li>Check that you can see your Solana address (it should start with a different format than Ethereum addresses)</li>
          <li>Click "Try Again" below to attempt the connection again</li>
        </ol>
      </div>
      
      <div className="guide-buttons">
        <button className="guide-button primary" onClick={onTryAgain}>
          Try Again
        </button>
        <button className="guide-button secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/**
 * Renders an improved OKX chain selection component with better guidance
 * @param {Object} props - Component props
 * @param {Function} props.onSelectEthereum - Callback when Ethereum is selected
 * @param {Function} props.onSelectSolana - Callback when Solana is selected
 * @param {Function} props.onCancel - Callback when "Cancel" is clicked
 * @returns {JSX.Element} OKX chain selection component
 */
export const OkxChainSelection = ({ onSelectEthereum, onSelectSolana, onCancel }) => (
  <div className="okx-chain-selection">
    <h3>Select Chain for OKX Wallet</h3>
    
    <p>OKX wallet supports both Ethereum/EVM chains and Solana. Please select which blockchain you want to connect with:</p>
    
    <div className="chain-selection-buttons">
      <button className="chain-button evm" onClick={onSelectEthereum}>
        <div className="chain-content">
          <span className="chain-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1.75L5.75 12.25L12 16L18.25 12.25L12 1.75Z" fill="#627EEA"/>
              <path d="M12 16L5.75 12.25L12 22.25L18.25 12.25L12 16Z" fill="#627EEA"/>
              <path d="M12 1.75V16L18.25 12.25L12 1.75Z" fill="#3C3C3B" fillOpacity="0.15"/>
              <path d="M12 22.25V16L18.25 12.25L12 22.25Z" fill="#3C3C3B" fillOpacity="0.15"/>
            </svg>
          </span>
          <div className="chain-details">
            <span className="chain-name">Ethereum/EVM</span>
            <span className="chain-desc">For Ethereum, BSC, and other EVM networks</span>
          </div>
        </div>
      </button>
      
      <button className="chain-button solana" onClick={onSelectSolana}>
        <div className="chain-content">
          <span className="chain-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.25 15.5L12 20.25L18.75 15.5" stroke="#9945FF" strokeWidth="2" strokeLinecap="round"/>
              <path d="M5.25 12L12 16.75L18.75 12" stroke="#9945FF" strokeWidth="2" strokeLinecap="round"/>
              <path d="M5.25 8.5L12 13.25L18.75 8.5" stroke="#9945FF" strokeWidth="2" strokeLinecap="round"/>
              <path d="M5.25 5L12 9.75L18.75 5" stroke="#9945FF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </span>
          <div className="chain-details">
            <span className="chain-name">Solana</span>
            <span className="chain-desc">For the Solana blockchain network</span>
          </div>
        </div>
      </button>
    </div>
    
    <div className="chain-selection-note">
      <p><strong>Note:</strong> To use Solana, you must have enabled Solana network in OKX wallet settings and created a Solana account.</p>
    </div>
    
    <button className="cancel-button" onClick={onCancel}>
      Cancel
    </button>
  </div>
);

/**
 * Function to get wallet icon based on type
 * @param {string} type - The wallet type
 * @returns {JSX.Element} Wallet icon component
 */
export const getWalletIcon = (type) => {
  switch (type.toLowerCase()) {
    case 'okx':
      return (
        <div className="wallet-logo okx-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#FFFFFF"/>
            <path d="M23 9H17V15H23V9Z" fill="#000000"/>
            <path d="M15 9H9V15H15V9Z" fill="#000000"/>
            <path d="M23 17H17V23H23V17Z" fill="#000000"/>
            <path d="M15 17H9V23H15V17Z" fill="#000000"/>
          </svg>
        </div>
      );
    case 'binance':
      return (
        <div className="wallet-logo binance-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#F0B90B"/>
            <path d="M16 7L18.5 9.5L13.5 14.5L11 12L16 7Z" fill="white"/>
            <path d="M19.5 10.5L22 13L13.5 21.5L11 19L19.5 10.5Z" fill="white"/>
            <path d="M10 13L12.5 15.5L10 18L7.5 15.5L10 13Z" fill="white"/>
            <path d="M22 19L24.5 21.5L22 24L19.5 21.5L22 19Z" fill="white"/>
          </svg>
        </div>
      );
    case 'metamask':
      return (
        <div className="wallet-logo metamask-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#FFFFFF"/>
            <path d="M27.5933 3L17.6 11.1867L19.4133 6.4L27.5933 3Z" fill="#E17726"/>
            <path d="M4.4 3L14.3067 11.28L12.5867 6.4L4.4 3Z" fill="#E27625"/>
            <path d="M23.8933 21.6133L21.0667 25.9867L26.96 27.6267L28.6667 21.7067L23.8933 21.6133Z" fill="#E27625"/>
            <path d="M3.34666 21.7067L5.04 27.6267L10.9333 25.9867L8.10666 21.6133L3.34666 21.7067Z" fill="#E27625"/>
          </svg>
        </div>
      );
    case 'phantom':
      return (
        <div className="wallet-logo phantom-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#551BF9"/>
            <path d="M23 10H15C12.2 10 10 12.2 10 15C10 17.8 12.2 20 15 20H17V22H21V16H15C14.4 16 14 15.6 14 15C14 14.4 14.4 14 15 14H23V10Z" fill="white"/>
          </svg>
        </div>
      );
    case 'trust':
      return (
        <div className="wallet-logo trust-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#FFFFFF"/>
            <path d="M16 4L25 8.5V15.5C25 20.5 21 24.5 16 26.5C11 24.5 7 20.5 7 15.5V8.5L16 4Z" fill="#0500FF"/>
            <path d="M13 15.5L15.5 18L20 13.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    case 'coinbase':
      return (
        <div className="wallet-logo coinbase-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#0052FF"/>
            <path d="M16 6C10.48 6 6 10.48 6 16C6 21.52 10.48 26 16 26C21.52 26 26 21.52 26 16C26 10.48 21.52 6 16 6ZM16 20.8C13.36 20.8 11.2 18.64 11.2 16C11.2 13.36 13.36 11.2 16 11.2C18.64 11.2 20.8 13.36 20.8 16C20.8 18.64 18.64 20.8 16 20.8Z" fill="white"/>
          </svg>
        </div>
      );
    case 'walletconnect':
      return (
        <div className="wallet-logo walletconnect-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#3B99FC"/>
            <path d="M9.58583 12.2855C13.2479 8.71201 19.1937 8.71201 22.8558 12.2855L23.2692 12.6886C23.4479 12.8638 23.4479 13.153 23.2692 13.3282L21.6137 14.9486C21.5244 15.0362 21.3795 15.0362 21.2902 14.9486L20.7191 14.3912C18.2 11.9331 14.2416 11.9331 11.7225 14.3912L11.1137 14.9863C11.0244 15.0739 10.8795 15.0739 10.7902 14.9863L9.13468 13.366C8.95597 13.1907 8.95597 12.9015 9.13468 12.7263L9.58583 12.2855ZM25.981 15.3555L27.4684 16.8132C27.6471 16.9884 27.6471 17.2776 27.4684 17.4528L21.0397 23.7302C20.8609 23.9054 20.5713 23.9054 20.3925 23.7302L15.9825 19.4062C15.9379 19.3624 15.8654 19.3624 15.8207 19.4062L11.4108 23.7302C11.232 23.9054 10.9423 23.9054 10.7636 23.7302C10.7636 23.7302 10.7636 23.7302 10.7636 23.7302L4.32913 17.4528C4.15042 17.2776 4.15042 16.9884 4.32913 16.8132L5.81657 15.3555C5.99528 15.1802 6.28495 15.1802 6.46366 15.3555L10.8736 19.6794C10.9183 19.7232 10.9908 19.7232 11.0354 19.6794L15.4453 15.3555C15.6241 15.1802 15.9137 15.1802 16.0924 15.3555C16.0924 15.3555 16.0924 15.3555 16.0924 15.3555L20.5023 19.6794C20.547 19.7232 20.6195 19.7232 20.6641 19.6794L25.074 15.3555C25.2527 15.1802 25.5424 15.1802 25.7211 15.3555L25.981 15.3555Z" fill="white"/>
          </svg>
        </div>
      );
    case 'ledger':
      return (
        <div className="wallet-logo ledger-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#000000"/>
            <path d="M9 9H15V23H9V9Z" fill="white"/>
            <path d="M17 9H23V15H17V9Z" fill="white"/>
            <path d="M17 17H23V23H17V17Z" fill="white"/>
          </svg>
        </div>
      );
    case 'solflare':
      return (
        <div className="wallet-logo solflare-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#FFA43A"/>
            <path d="M21.75 12.96L16.98 8.19C16.59 7.8 15.95 7.8 15.56 8.19L10.79 12.96C10.4 13.35 10.4 13.99 10.79 14.38L15.56 19.15C15.95 19.54 16.59 19.54 16.98 19.15L21.75 14.38C22.14 13.99 22.14 13.35 21.75 12.96Z" fill="white"/>
            <path d="M10.79 19.6L8.19 22.2C7.8 22.59 7.8 23.23 8.19 23.62L10.79 26.22C11.18 26.61 11.82 26.61 12.21 26.22L14.81 23.62C15.2 23.23 15.2 22.59 14.81 22.2L12.21 19.6C11.82 19.21 11.18 19.21 10.79 19.6Z" fill="white"/>
          </svg>
        </div>
      );
    default:
      return (
        <div className="wallet-logo other-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="6" fill="#888888"/>
            <path d="M16 8C17.1 8 18 8.9 18 10C18 11.1 17.1 12 16 12C14.9 12 14 11.1 14 10C14 8.9 14.9 8 16 8ZM16 14C17.1 14 18 14.9 18 16C18 17.1 17.1 18 16 18C14.9 18 14 17.1 14 16C14 14.9 14.9 14 16 14ZM16 20C17.1 20 18 20.9 18 22C18 23.1 17.1 24 16 24C14.9 24 14 23.1 14 22C14 20.9 14.9 20 16 20Z" fill="white"/>
          </svg>
        </div>
      );
  }
};

/**
 * Helper to categorize wallet errors for better error handling
 * @param {string} errorMessage - The error message
 * @returns {string} Error category
 */
export const categorizeWalletError = (errorMessage) => {
  const message = errorMessage.toLowerCase();
  
  if (message.includes('user rejected') || 
      message.includes('user denied') || 
      message.includes('not authorized')) {
    return 'USER_REJECTED';
  }
  
  if (message.includes('not found') || 
      message.includes('provider not available') ||
      message.includes('provider missing')) {
    return 'PROVIDER_MISSING';
  }
  
  if (message.includes('public key') ||
      message.includes('account') ||
      message.includes('address')) {
    return 'ACCOUNT_ACCESS';
  }
  
  if (message.includes('timeout') ||
      message.includes('timed out')) {
    return 'TIMEOUT';
  }
  
  if (message.includes('network') ||
      message.includes('chain')) {
    return 'NETWORK_ERROR';
  }
  
  return 'UNKNOWN';
};

/**
 * Get the user-friendly error message based on error category and wallet type
 * @param {string} errorCategory - The error category 
 * @param {string} walletType - The wallet type
 * @param {string} chain - The preferred chain ('solana', 'ethereum', etc.)
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (errorCategory, walletType, chain = null) => {
  const walletName = walletType.charAt(0).toUpperCase() + walletType.slice(1);
  
  switch (errorCategory) {
    case 'USER_REJECTED':
      return `You rejected the connection request. Please approve the request in your ${walletName} wallet to connect.`;
      
    case 'PROVIDER_MISSING':
      if (walletType.toLowerCase() === 'okx' && chain === 'solana') {
        return 'Unable to find OKX Solana provider. Please make sure Solana is enabled in your OKX wallet settings.';
      }
      return `${walletName} wallet extension not detected. Please install or unlock the wallet and refresh the page.`;
      
    case 'ACCOUNT_ACCESS':
      if (walletType.toLowerCase() === 'okx' && chain === 'solana') {
        return 'Could not access your Solana account. Please make sure you have created a Solana account in OKX wallet.';
      }
      return `Could not access account in ${walletName} wallet. Please check that you're logged in and try again.`;
      
    case 'TIMEOUT':
      return `Connection to ${walletName} wallet timed out. Please try again.`;
      
    case 'NETWORK_ERROR':
      return `Network error connecting to ${walletName} wallet. Please check your internet connection and try again.`;
      
    default:
      return `Failed to connect ${walletName} wallet. Please try again.`;
  }
};

/**
 * Get wallet information tooltip content
 * @param {string} walletType - The wallet type
 * @returns {JSX.Element} Tooltip content
 */
export const getWalletTooltip = (walletType) => {
  const type = walletType.toLowerCase();
  
  switch (type) {
    case 'okx':
      return (
        <div className="wallet-tooltip">
          <h4>OKX Wallet</h4>
          <p>Supports both Ethereum/EVM chains and Solana.</p>
          <p>You will need to enable Solana network in the wallet settings to use Solana.</p>
          <a href="https://www.okx.com/web3" target="_blank" rel="noopener noreferrer">Learn more</a>
        </div>
      );
    case 'phantom':
      return (
        <div className="wallet-tooltip">
          <h4>Phantom Wallet</h4>
          <p>Popular wallet for Solana blockchain.</p>
          <a href="https://phantom.app" target="_blank" rel="noopener noreferrer">Learn more</a>
        </div>
      );
    case 'metamask':
      return (
        <div className="wallet-tooltip">
          <h4>MetaMask</h4>
          <p>Popular wallet for Ethereum and other EVM chains.</p>
          <a href="https://metamask.io" target="_blank" rel="noopener noreferrer">Learn more</a>
        </div>
      );
    default:
      return (
        <div className="wallet-tooltip">
          <h4>{walletType.charAt(0).toUpperCase() + walletType.slice(1)} Wallet</h4>
          <p>Connect to continue to the AeroFi network.</p>
        </div>
      );
  }
};

// Additional helper components for the WalletConnector.jsx

/**
 * Component to display a wallet error message
 * @param {Object} props - Component props
 * @param {string} props.message - Error message
 * @param {Function} props.onDismiss - Optional callback when error is dismissed
 * @returns {JSX.Element} Error message component
 */
export const WalletError = ({ message, onDismiss }) => (
  <div className="wallet-error" role="alert">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#ff5252" strokeWidth="2"/>
      <path d="M12 8V12" stroke="#ff5252" strokeWidth="2" strokeLinecap="round"/>
      <path d="M12 16H12.01" stroke="#ff5252" strokeWidth="2" strokeLinecap="round"/>
    </svg>
    <p>{message}</p>
    {onDismiss && (
      <button className="error-dismiss" onClick={onDismiss} aria-label="Dismiss error">
        ×
      </button>
    )}
  </div>
);

/**
 * Component to display network filter tabs
 * @param {Object} props - Component props
 * @param {string} props.activeFilter - Currently active filter ('all', 'ethereum', 'bsc', 'solana')
 * @param {Function} props.onChange - Callback when filter changes
 * @returns {JSX.Element} Network filter tabs component
 */
export const NetworkFilterTabs = ({ activeFilter, onChange }) => (
  <div className="network-filter-tabs">
    <button 
      className={`network-tab ${activeFilter === 'all' ? 'active' : ''}`}
      onClick={() => onChange('all')}
    >
      All Networks
    </button>
    <button 
      className={`network-tab ${activeFilter === 'ethereum' ? 'active' : ''}`}
      onClick={() => onChange('ethereum')}
    >
      Ethereum
    </button>
    <button 
      className={`network-tab ${activeFilter === 'bsc' ? 'active' : ''}`}
      onClick={() => onChange('bsc')}
    >
      BSC
    </button>
    <button 
      className={`network-tab ${activeFilter === 'solana' ? 'active' : ''}`}
      onClick={() => onChange('solana')}
    >
      Solana
    </button>
  </div>
);

/**
 * Component to display information about wallet compatibility
 * @param {Object} props - Component props
 * @param {Object} props.supportInfo - Wallet support information
 * @param {string} props.networkFilter - Current network filter
 * @returns {JSX.Element} Wallet support info component
 */
export const WalletSupportInfo = ({ supportInfo, networkFilter }) => (
  <div className="chain-support-info">
    <div className={`support-info-box ${supportInfo.supportsEVM && supportInfo.supportsSolana ? 'dual' : (supportInfo.supportsSolana ? 'solana' : 'evm')}`}>
      <div className="support-info-icon">
        {supportInfo.supportsEVM && supportInfo.supportsSolana ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 19V19.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </div>
      <div className="support-info-text">
        <p>{supportInfo.supportInfo}</p>
        {networkFilter !== 'all' && (
          <p className="support-warning">
            {networkFilter === 'ethereum' && !supportInfo.supportsEVM && 
              "Warning: You've selected Ethereum filter, but this wallet only supports Solana!"
            }
            {networkFilter === 'bsc' && !supportInfo.supportsEVM && 
              "Warning: You've selected BSC filter, but this wallet only supports Solana!"
            }
            {networkFilter === 'solana' && !supportInfo.supportsSolana && 
              "Warning: You've selected Solana filter, but this wallet only supports EVM chains!"
            }
          </p>
        )}
      </div>
    </div>
  </div>
);
